import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from './AuthContext';

const UsageContext = createContext(null);

export const useUsage = () => {
  const context = useContext(UsageContext);
  if (!context) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
};

export const UsageProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [usageData, setUsageData] = useState({
    // Daily usage
    imageLimit: 10,
    gifLimit: 10,
    imageUsed: 0,
    gifUsed: 0,
    imageRemaining: 10,
    gifRemaining: 10,
    // Monthly usage
    monthlyImageLimit: 600,
    monthlyGifLimit: 200,
    monthlyImageUsed: 0,
    monthlyGifUsed: 0,
    monthlyImageRemaining: 600,
    monthlyGifRemaining: 200,
    date: null
  });
  const [loading, setLoading] = useState(false);

  // 사용량 데이터 가져오기
  const fetchUsage = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get('/usage/today');
      const data = response.data;

      // daily_usage에서 cardnews와 gif 카운트 추출
      const dailyCardnewsCount = data.daily_usage?.find(
        item => item.feature_type === 'cardnews'
      )?.count || 0;

      const dailyGifCount = data.daily_usage?.find(
        item => item.feature_type === 'gif'
      )?.count || 0;

      // monthly_usage에서 cardnews와 gif 카운트 추출
      const monthlyCardnewsCount = data.monthly_usage?.find(
        item => item.feature_type === 'cardnews'
      )?.count || 0;

      const monthlyGifCount = data.monthly_usage?.find(
        item => item.feature_type === 'gif'
      )?.count || 0;

      // Daily limits
      const dailyImageLimit = data.daily_image_limit || 10;
      const dailyGifLimit = data.daily_gif_limit || 10;

      // Monthly limits
      const monthlyImageLimit = data.monthly_image_limit || 600;
      const monthlyGifLimit = data.monthly_gif_limit || 200;

      setUsageData({
        // Daily usage
        imageLimit: dailyImageLimit,
        gifLimit: dailyGifLimit,
        imageUsed: dailyCardnewsCount,
        gifUsed: dailyGifCount,
        imageRemaining: dailyImageLimit - dailyCardnewsCount,
        gifRemaining: dailyGifLimit - dailyGifCount,
        // Monthly usage
        monthlyImageLimit: monthlyImageLimit,
        monthlyGifLimit: monthlyGifLimit,
        monthlyImageUsed: monthlyCardnewsCount,
        monthlyGifUsed: monthlyGifCount,
        monthlyImageRemaining: monthlyImageLimit - monthlyCardnewsCount,
        monthlyGifRemaining: monthlyGifLimit - monthlyGifCount,
        date: data.date
      });
    } catch (error) {
      console.error('사용량 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // 로그인 시 초기 데이터 로드
  useEffect(() => {
    if (isAuthenticated) {
      fetchUsage();
    }
  }, [isAuthenticated, fetchUsage]);

  // 사용량 갱신 함수 (외부에서 호출 가능)
  const refreshUsage = useCallback(() => {
    return fetchUsage();
  }, [fetchUsage]);

  const value = {
    ...usageData,
    loading,
    refreshUsage
  };

  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
};
